import React, { useEffect, useState, useMemo } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import DashboardShell from "@/components/dashboard/DashboardShell";
import api from "@/lib/api";
import CreatorWizard from "@/components/templates/CreatorWizard";
import { Coins, Sparkles, Search, Play } from "lucide-react";

const QUALITY_BADGE = {
  draft:  { label: "Draft",  cls: "bg-blue-500/10 text-blue-300 border-blue-500/40" },
  pro:    { label: "Pro",    cls: "bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/40" },
  cinema: { label: "Cinema", cls: "bg-purple-500/10 text-purple-300 border-purple-500/40" },
};

export default function TemplatesPage() {
  const { user, loading } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [activeCategory, setActiveCategory] = useState("All");
  const [search, setSearch] = useState("");
  const [active, setActive] = useState(null); // selected template for wizard

  useEffect(() => {
    if (user) api.get("/templates").then(({ data }) => setTemplates(data.templates || []));
  }, [user]);

  const categories = useMemo(() => ["All", ...Array.from(new Set(templates.map((t) => t.category)))], [templates]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return templates.filter((t) =>
      (activeCategory === "All" || t.category === activeCategory) &&
      (!q || t.name.toLowerCase().includes(q) || t.starter_prompt.toLowerCase().includes(q) || (t.editing_style || "").toLowerCase().includes(q))
    );
  }, [templates, activeCategory, search]);

  const countByCategory = useMemo(() => {
    const m = {};
    templates.forEach((t) => { m[t.category] = (m[t.category] || 0) + 1; });
    return m;
  }, [templates]);

  if (loading || user === undefined) return null;
  if (!user) return <Navigate to="/login?next=/templates" replace />;

  return (
    <DashboardShell>
      <div className="mb-8">
        <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#D4AF37] mb-3">Templates · Visual Gallery</p>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter">Pick a look. Make it yours.</h1>
        <p className="text-[#A0A0A0] mt-4 max-w-2xl">
          {templates.length} ready-to-render templates across {categories.length - 1} categories. Click any card to open the creator wizard — upload your own photo or generate from scratch, add voiceover, and ship.
        </p>
      </div>

      {/* Search */}
      <div className="mb-5 relative max-w-xl">
        <Search className="w-4 h-4 text-[#5A5A5A] absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search templates by name, mood, style…"
          data-testid="templates-search"
          className="w-full bg-[#0A0A0A] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none pl-10 pr-4 py-3 text-sm text-[#F4F4F0] placeholder-[#5A5A5A] transition-colors"
        />
      </div>

      {/* Category rail */}
      <div className="mb-8 flex flex-wrap gap-2" data-testid="template-categories">
        {categories.map((c) => {
          const count = c === "All" ? templates.length : countByCategory[c] || 0;
          return (
            <button
              key={c}
              onClick={() => setActiveCategory(c)}
              data-testid={`category-chip-${c}`}
              className={`px-3.5 py-1.5 text-[10px] uppercase tracking-[0.2em] border transition-colors flex items-center gap-1.5 ${
                activeCategory === c
                  ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/5"
                  : "border-[#2A2A2A] text-[#A0A0A0] hover:text-[#F4F4F0]"
              }`}
            >
              {c} <span className="text-[#5A5A5A] font-mono">{count}</span>
            </button>
          );
        })}
      </div>

      {/* Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="templates-grid">
        {filtered.map((t, idx) => {
          const badge = QUALITY_BADGE[t.recommended_quality] || QUALITY_BADGE.pro;
          // Stagger drift duration so the wall feels organic, not metronomic.
          const driftDur = 7 + (idx % 5) * 0.6;
          return (
            <button
              key={t.id}
              onClick={() => setActive(t)}
              data-testid={`template-card-${t.id}`}
              className="group text-left bg-[#0A0A0A] border border-[#1F1F1F] hover:border-[#D4AF37]/50 transition-all duration-200 overflow-hidden flex flex-col hover:-translate-y-0.5"
            >
              <div className="relative aspect-[4/5] bg-[#121212] overflow-hidden">
                {/* Continuous ken-burns drift gives the still a 'video preview' feel.
                    Scales up further + speeds animation on hover. */}
                <img
                  src={t.thumbnail_url}
                  alt={t.name}
                  loading="lazy"
                  className="w-full h-full object-cover ken-burns group-hover:ken-burns-hover"
                  style={{ animationDuration: `${driftDur}s` }}
                />
                {/* Hover play overlay + scanline shimmer make it read as a clip preview */}
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                  <div className="w-14 h-14 rounded-full bg-[#D4AF37]/95 text-[#080808] flex items-center justify-center shadow-2xl pulse-glow">
                    <Play className="w-6 h-6 ml-1" fill="currentColor" />
                  </div>
                </div>
                <div aria-hidden className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 scanline-overlay" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
                <div className="absolute top-3 left-3 flex items-center gap-1">
                  <span className={`px-2 py-0.5 text-[9px] uppercase tracking-wider border ${badge.cls}`}>{badge.label}</span>
                </div>
                <div className="absolute top-3 right-3">
                  <span className="px-2 py-0.5 text-[9px] uppercase tracking-wider text-[#F4F4F0]/80 bg-black/40 border border-white/10 backdrop-blur-sm">
                    {t.default_aspect} · {t.default_duration}s
                  </span>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <p className="text-[9px] uppercase tracking-wider text-[#D4AF37] mb-1">{t.category}</p>
                  <h3 className="font-display text-base font-bold text-[#F4F4F0] leading-tight">{t.name}</h3>
                </div>
              </div>
              <div className="px-4 py-3 border-t border-[#1F1F1F]">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[9px] uppercase tracking-wider text-[#A0A0A0]">Credits per render</p>
                  <span className="text-[10px] text-[#A0A0A0] uppercase tracking-wider inline-flex items-center gap-1 group-hover:text-[#D4AF37] transition-colors">
                    Customize <Sparkles className="w-3 h-3" />
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-1.5" data-testid={`quality-chips-${t.id}`}>
                  {[
                    { id: "draft",  label: "Draft",  cls: "border-blue-500/40 text-blue-300" },
                    { id: "pro",    label: "Pro",    cls: "border-[#D4AF37]/50 text-[#D4AF37]" },
                    { id: "cinema", label: "Cinema", cls: "border-purple-500/40 text-purple-300" },
                  ].map((q) => {
                    const cost = (t.credit_estimates && t.credit_estimates[q.id]) ?? t.credit_estimate;
                    const isRec = t.recommended_quality === q.id;
                    return (
                      <div key={q.id}
                           className={`text-center px-1.5 py-1 border ${q.cls} bg-[#0A0A0A] ${isRec ? "ring-1 ring-[#D4AF37]/40" : ""}`}
                           data-testid={`quality-chip-${t.id}-${q.id}`}>
                        <p className="text-[9px] uppercase tracking-wider opacity-80">{q.label}</p>
                        <p className="text-[11px] font-bold inline-flex items-center justify-center gap-0.5">
                          <Coins className="w-2.5 h-2.5" /> {cost}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-12 text-[#A0A0A0] text-sm" data-testid="templates-empty">
          No templates match "{search}" in {activeCategory}.
        </div>
      )}

      <CreatorWizard template={active} user={user} onClose={() => setActive(null)} />
    </DashboardShell>
  );
}
